/**
 * Deal contracts. Destination: `@renote/contracts`.
 *
 * Note what is absent from the input schemas: `wonAt` and `lostAt`. Those are
 * derived from the status's outcome by database trigger, so a caller that could
 * set them would be able to create a deal whose status and outcome disagree.
 */

import { z } from 'zod'
import {
  arrayParam,
  boolParam,
  jsonParam,
  viewFilter,
  currencyCode,
  instant,
  pageOf,
  pageQuery,
  sortDirection,
  trimmedText,
  uuid,
} from './common'

export const dealStatusOutcome = z.enum(['won', 'lost'])
export type DealStatusOutcome = z.infer<typeof dealStatusOutcome>

export const dealStatus = z.object({
  id: uuid,
  name: z.string(),
  color: z.string(),
  position: z.number().int(),
  isDefault: z.boolean(),
  outcome: dealStatusOutcome.nullable(),
})
export type DealStatus = z.infer<typeof dealStatus>

export const deal = z.object({
  id: uuid,
  organizationId: uuid.nullable(),
  primaryContactId: uuid.nullable(),
  statusId: uuid,
  sourceId: uuid.nullable(),
  ownerId: uuid.nullable(),
  amount: z.number().nonnegative().nullable(),
  currency: z.string().nullable(),
  probability: z.number().int().min(0).max(100).nullable(),
  expectedCloseDate: z.string().date().nullable(),
  wonAt: instant.nullable(),
  lostAt: instant.nullable(),
  lostReasonId: uuid.nullable(),
  convertedFromLeadId: uuid.nullable(),
  createdBy: uuid,
  createdAt: instant,
  updatedAt: instant,
})
export type Deal = z.infer<typeof deal>

export const createDealInput = z
  .object({
    organizationId: uuid.nullable(),
    primaryContactId: uuid.nullable(),
    statusId: uuid.optional(), // defaults to crm_deal_status.is_default
    sourceId: uuid.nullable(),
    ownerId: uuid.nullable(),
    amount: z.number().nonnegative().nullable(),
    currency: currencyCode.nullable(),
    probability: z.number().int().min(0).max(100).nullable(),
    expectedCloseDate: z.string().date().nullable(),
  })
  .strict()
  // mirrors crm_deal_amount_has_currency
  .refine((v) => v.amount === null || v.currency !== null, {
    message: 'Currency is required when an amount is set',
    path: ['currency'],
  })
export type CreateDealInput = z.infer<typeof createDealInput>

/**
 * `statusId` is omitted, not merely optional: stage changes go through
 * `changeDealStatusInput` so a losing stage can carry its reason in the same
 * statement. Leaving it accepted here would let a caller send a status the
 * repository silently ignores — a contract that lies about what the server does.
 */
export const updateDealInput = createDealInput
  .innerType()
  .omit({ statusId: true })
  .partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' })
  /** Mirrors crm_deal_amount_has_currency; judged only when both fields are present. */
  .refine((v) => !('amount' in v && 'currency' in v) || v.amount === null || v.currency !== null, {
    message: 'Currency is required when an amount is set',
    path: ['currency'],
  })
export type UpdateDealInput = z.infer<typeof updateDealInput>

/**
 * Status change is its own endpoint rather than a field on update, because
 * moving a deal to a lost status requires a reason in the same statement — the
 * crm_deal_lost_needs_reason constraint will reject it otherwise.
 */
export const changeDealStatusInput = z
  .object({
    statusId: uuid,
    lostReasonId: uuid.nullable(),
  })
  .strict()
export type ChangeDealStatusInput = z.infer<typeof changeDealStatusInput>

/**
 * Lead conversion. One endpoint, one UnitOfWork: it creates the organization and
 * contact when they are new, inserts the deal, and stamps the lead's
 * converted_at / converted_deal_id. Splitting it across calls would leave a
 * half-converted lead if the second call failed.
 */
const convertLeadShape = {
  /** Reuse an existing organization, or create one from the lead's free text. */
  organizationId: uuid.nullable(),
  newOrganizationName: trimmedText(200),
  /** Same choice for the person. */
  contactId: uuid.nullable(),
  createContactFromLead: z.boolean().default(true),
  deal: createDealInput.innerType().partial().strict(),
}

const ORGANIZATION_RULE = {
  message: 'Select an existing organization or provide a name for a new one',
  path: ['organizationId'],
}

const CONTACT_RULE = {
  message: 'Select an existing contact or create one from the lead',
  path: ['contactId'],
}

/**
 * The request body. `leadId` comes from the path, so it is deliberately absent
 * here — validating a schema that required it would reject every real request,
 * since the controller merges the param only after the pipe has run.
 */
export const convertLeadBody = z
  .object(convertLeadShape)
  .strict()
  .refine((v) => v.organizationId !== null || v.newOrganizationName !== null, ORGANIZATION_RULE)
  .refine((v) => v.contactId !== null || v.createContactFromLead, CONTACT_RULE)
export type ConvertLeadBody = z.infer<typeof convertLeadBody>

/** What the service receives, once the path param is merged in. */
export const convertLeadInput = z
  .object({ ...convertLeadShape, leadId: uuid })
  .strict()
  .refine((v) => v.organizationId !== null || v.newOrganizationName !== null, ORGANIZATION_RULE)
  .refine((v) => v.contactId !== null || v.createContactFromLead, CONTACT_RULE)
export type ConvertLeadInput = z.infer<typeof convertLeadInput>

export const dealContact = z.object({
  dealId: uuid,
  contactId: uuid,
  role: z.string().nullable(),
  isPrimary: z.boolean(),
})
export type DealContact = z.infer<typeof dealContact>

export const dealSortField = z.enum([
  'updatedAt',
  'createdAt',
  'amount',
  'expectedCloseDate',
  'statusId',
])

export const dealListQuery = pageQuery
  .extend({
    search: z.string().trim().max(200).optional(),
    statusId: arrayParam(uuid).optional(),
    ownerId: arrayParam(uuid).optional(),
    organizationId: arrayParam(uuid).optional(),
    sourceId: arrayParam(uuid).optional(),
    /** 'open' excludes won and lost; the default the pipeline view wants. */
    state: z.enum(['open', 'won', 'lost', 'all']).default('open'),
    /**
     * Ad-hoc filters from the view builder, validated against the entity's field
     * registry server-side before compilation. The named facets above stay for
     * the common cases and for hand-written links.
     */
    filters: jsonParam(z.array(viewFilter)).optional(),
    closingBefore: z.string().date().optional(),
    sort: dealSortField.default('updatedAt'),
    direction: sortDirection.default('desc'),
  })
  .strict()
export type DealListQuery = z.infer<typeof dealListQuery>

export const dealListResult = pageOf(deal)
export type DealListResult = z.infer<typeof dealListResult>

/**
 * A kanban column. `key` is the raw field value the column represents — the
 * empty string standing in for null, since a query param cannot carry one — and
 * `label`/`color` are resolved server-side from whichever lookup the group-by
 * field draws on.
 */
export const boardColumn = z.object({
  key: z.string(),
  label: z.string(),
  color: z.string().nullable(),
  /** Only set when grouping by status; the board uses it to route lost moves. */
  outcome: dealStatusOutcome.nullable(),
  rows: z.array(deal),
  totalCount: z.number().int().nonnegative(),
  /**
   * Null when the column mixes currencies. Summing £130,000 and $215,000 into
   * one number is not a smaller number, it is a wrong one — so the board shows
   * nothing rather than something misleading.
   */
  totalAmount: z.number().nonnegative().nullable(),
  totalCurrency: z.string().nullable(),
  nextCursor: z.string().nullable(),
})
export type BoardColumn = z.infer<typeof boardColumn>

export const dealBoard = z.object({
  groupBy: z.string(),
  columns: z.array(boardColumn),
})
export type DealBoard = z.infer<typeof dealBoard>

/**
 * Board query. `groupBy` must name a groupable field — a bounded option set —
 * or the registry rejects it; grouping by an unbounded link would render a
 * column per organization.
 */
export const dealBoardQuery = z
  .object({
    groupBy: z.string().max(64).default('statusId'),
    filters: jsonParam(z.array(viewFilter)).optional(),
    state: z.enum(['open', 'won', 'lost', 'all']).default('open'),
    showEmptyColumns: boolParam.default(true),
  })
  .strict()
export type DealBoardQuery = z.infer<typeof dealBoardQuery>
